import { useEffect, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ConfirmDeleteDialog } from "@/components/ui/confirm-delete-dialog";
import { useClaimPhotos } from "@/hooks/use-claim-photos";
import { PhotoLightbox } from "./PhotoLightbox";
import {
    Image as ImageIcon, ChevronDown, ChevronRight, Upload, Loader2,
    Download, Trash2, Play,
} from "lucide-react";
import type { ClaimPhoto } from "@/api/generated/client/models";

const ACCEPTED_TYPES =
    "image/jpeg,image/png,image/gif,image/webp,image/bmp,image/avif,video/mp4,video/webm";

interface PhotosModuleProps {
    claimId: number;
    isOpen: boolean;
    onToggle: () => void;
    canWrite: boolean;
}

function isVideo(mimeType: string): boolean {
    return mimeType.startsWith("video/");
}

interface ThumbProps {
    photo: ClaimPhoto;
    onOpen: () => void;
    getPhotoThumbnailBlob: (id: number, signal?: AbortSignal) => Promise<Blob>;
}

function PhotoThumbnail({ photo, onOpen, getPhotoThumbnailBlob }: ThumbProps) {
    const thumbnailRef = useRef<HTMLButtonElement>(null);
    const [thumbUrl, setThumbUrl] = useState<string | null>(null);
    const [isVisible, setIsVisible] = useState(false);
    const video = isVideo(photo.mimeType);

    useEffect(() => {
        if (video) return;
        const thumbnail = thumbnailRef.current;
        if (!thumbnail || !window.IntersectionObserver) {
            setIsVisible(true);
            return;
        }

        const observer = new IntersectionObserver((entries) => {
            const entry = entries.find((candidate) => candidate.target === thumbnail);
            if (entry) setIsVisible(entry.isIntersecting);
        }, { rootMargin: "100px" });
        observer.observe(thumbnail);

        return () => observer.disconnect();
    }, [video]);

    useEffect(() => {
        if (video || !isVisible) return;
        let cancelled = false;
        let objectUrl: string | null = null;
        const controller = new AbortController();
        (async () => {
            try {
            const blob = await getPhotoThumbnailBlob(photo.id, controller.signal);
                if (cancelled) return;
                objectUrl = URL.createObjectURL(blob);
                setThumbUrl(objectUrl);
            } catch {
                /* ignore thumbnail failure */
            }
        })();
        return () => {
            cancelled = true;
            controller.abort();
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
                setThumbUrl(null);
            }
        };
    }, [photo.id, video, isVisible, getPhotoThumbnailBlob]);

    return (
        <button
            type="button"
            ref={thumbnailRef}
            onClick={onOpen}
            title={photo.caption || photo.filename}
            className="group relative aspect-square w-full overflow-hidden rounded-md border bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
            data-testid={`photo-thumb-${photo.id}`}
        >
            {video ? (
                <div className="flex h-full w-full items-center justify-center bg-black/80">
                    <Play className="h-6 w-6 text-white/80" />
                </div>
            ) : thumbUrl ? (
                <img
                    src={thumbUrl}
                    alt={photo.caption || photo.filename}
                    className="h-full w-full object-cover transition-transform group-hover:scale-105"
                    loading="lazy"
                />
            ) : (
                <div className="flex h-full w-full items-center justify-center">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
            )}
        </button>
    );
}

export function PhotosModule({ claimId, isOpen, onToggle, canWrite }: PhotosModuleProps) {
    const {
        photos, isLoading, isUploading,
        getPhotoThumbnailBlob, downloadPhoto, isDeleting,
        updatePhotoAsync, isUpdating,
        fileInputRef, isDraggingFiles, handleFileChange, handleDragEnter,
        handleDragOver, handleDragLeave, handleDrop, openLightbox,
        lightboxOpen, activeIndex, handleNavigate, handleLightboxOpenChange,
        lightboxBlobUrl, lightboxLoading, lightboxError, deleteTargetId,
        setDeleteTargetId, handleDeleteConfirm,
    } = useClaimPhotos(claimId, canWrite);

    return (
        <Card className="shadow-xs w-full overflow-hidden">
            <Collapsible open={isOpen} onOpenChange={onToggle}>
                <CollapsibleTrigger className="w-full">
                    <CardHeader className="py-3 px-4 hover:bg-muted/50 cursor-pointer transition-colors overflow-hidden">
                        <div className="flex items-center justify-between min-w-0 w-full">
                            <div className="flex items-center gap-2 min-w-0">
                                <ImageIcon className="h-4 w-4 shrink-0" />
                                <CardTitle className="text-sm font-medium truncate">Photos</CardTitle>
                                <Badge variant="secondary" className="text-xs h-5 px-1.5 min-w-[1.25rem] shrink-0 flex items-center justify-center">
                                    {photos?.length || 0}
                                </Badge>
                            </div>
                            {isOpen ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
                        </div>
                    </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <CardContent
                        className={`relative px-4 pb-4 pt-0 w-full overflow-hidden ${isDraggingFiles ? "bg-muted/50" : ""}`}
                        onDragEnter={handleDragEnter}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        data-testid="photos-dropzone"
                    >
                        <div className="pb-2 border-b mb-3"></div>

                        {isDraggingFiles && (
                            <div className="absolute inset-0 z-10 flex items-center justify-center border-2 border-dashed border-primary bg-background/80 text-sm font-medium text-primary pointer-events-none">
                                Drop photos to upload
                            </div>
                        )}

                        {isLoading ? (
                            <div className="flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
                        ) : photos.length === 0 ? (
                            <p className="text-center py-6 text-xs text-muted-foreground">No photos yet</p>
                        ) : (
                            <div className="grid grid-cols-3 gap-2 max-h-[400px] overflow-y-auto overflow-x-hidden pr-0.5">
                                {photos.map((photo, index) => (
                                    <div key={photo.id} className="relative group">
                                        <PhotoThumbnail
                                            photo={photo}
                                            onOpen={() => openLightbox(index)}
                                            getPhotoThumbnailBlob={getPhotoThumbnailBlob}
                                        />
                                        <div className="absolute top-1 right-1 flex gap-1 opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
                                            <Button
                                                size="icon"
                                                variant="secondary"
                                                className="h-6 w-6 bg-background/80 backdrop-blur hover:bg-background"
                                                title="Download"
                                                onClick={(e) => { e.stopPropagation(); void downloadPhoto(photo.id, photo.filename); }}
                                            >
                                                <Download className="h-3 w-3" />
                                            </Button>
                                            {canWrite && (
                                                <Button
                                                    size="icon"
                                                    variant="secondary"
                                                    className="h-6 w-6 bg-background/80 backdrop-blur hover:bg-destructive hover:text-destructive-foreground"
                                                    title="Delete"
                                                    onClick={(e) => { e.stopPropagation(); setDeleteTargetId(photo.id); }}
                                                    disabled={isDeleting}
                                                >
                                                    <Trash2 className="h-3 w-3" />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Toolbar */}
                        <div className="pt-3 border-t mt-3 w-full">
                            <input
                                type="file"
                                ref={fileInputRef}
                                className="hidden"
                                accept={ACCEPTED_TYPES}
                                onChange={handleFileChange}
                                disabled={isUploading || !canWrite}
                                data-testid="photo-upload-input"
                                multiple
                            />
                            <Button
                                size="sm"
                                className="w-full h-7 text-xs gap-1.5"
                                onClick={() => { if (canWrite) fileInputRef.current?.click(); }}
                                disabled={isUploading || !canWrite}
                            >
                                {isUploading
                                    ? <><Loader2 className="h-3 w-3 animate-spin" />Uploading…</>
                                    : <><Upload className="h-3 w-3" />Upload Photos</>
                                }
                            </Button>
                        </div>
                    </CardContent>
                </CollapsibleContent>
            </Collapsible>

            <PhotoLightbox
                open={lightboxOpen}
                onOpenChange={handleLightboxOpenChange}
                photos={photos}
                activeIndex={activeIndex}
                onNavigate={handleNavigate}
                blobUrl={lightboxBlobUrl}
                isLoading={lightboxLoading}
                error={lightboxError}
                onDownload={(photo) => void downloadPhoto(photo.id, photo.filename)}
                canWrite={canWrite}
                onSaveCaption={async (photo, caption) => {
                    await updatePhotoAsync({ photoId: photo.id, caption });
                }}
                isSaving={isUpdating}
            />

            <ConfirmDeleteDialog
                open={deleteTargetId != null}
                onOpenChange={(open) => { if (!open) setDeleteTargetId(null); }}
                onConfirm={handleDeleteConfirm}
                title="Delete photo?"
                description="This photo will be removed from the claim. This action cannot be undone."
            />
        </Card>
    );
}
